import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { strings } from '../strings';
import { applyManualRedactions } from '../screenshot/redact';
import type { RedactionBox } from '../screenshot/redact';

export interface ScreenshotPreviewProps {
  /** The just-captured (or previously redacted) screenshot to review. */
  blob: Blob;
  /** Called with the final blob — redacted if any boxes were drawn, the original otherwise. */
  onConfirm: (finalBlob: Blob) => void;
  onCancel: () => void;
  /** Fires with the current box list on every add/clear — purely an observation hook. */
  onRedactBoxesChange?: (boxes: RedactionBox[]) => void;
}

/** Minimum drag distance (px, in either axis) for a gesture to register as a box rather than a stray click. */
const MIN_BOX_SIZE = 4;

interface Point {
  x: number;
  y: number;
}

function boxFromPoints(a: Point, b: Point): RedactionBox {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(a.x - b.x),
    h: Math.abs(a.y - b.y),
  };
}

/**
 * Shows the captured screenshot (`blob`, as an object URL — revoked on unmount/blob change)
 * and lets the user drag out rectangles over it to mark sensitive areas before attaching it.
 * Each completed drag appends one box; Confirm runs `applyManualRedactions` (only if any boxes
 * were drawn — an untouched screenshot round-trips as-is, no canvas work) and hands the
 * resulting blob to `onConfirm`. Rendered as a step inside `Modal` (see `Modal.tsx`), so it
 * relies on the ancestor's `<style>{styles}</style>` injection rather than including its own.
 */
export function ScreenshotPreview({ blob, onConfirm, onCancel, onRedactBoxesChange }: ScreenshotPreviewProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [boxes, setBoxes] = useState<RedactionBox[]>([]);
  const [dragStart, setDragStart] = useState<Point | null>(null);
  const [dragCurrent, setDragCurrent] = useState<Point | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [redactError, setRedactError] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const url = URL.createObjectURL(blob);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  // A new capture (different blob) starts from a clean redaction slate.
  useEffect(() => {
    setBoxes([]);
  }, [blob]);

  const reportBoxes = (next: RedactionBox[]) => {
    setBoxes(next);
    onRedactBoxesChange?.(next);
  };

  const pointFromEvent = (event: JSX.TargetedPointerEvent<HTMLDivElement>): Point => {
    const rect = canvasRef.current?.getBoundingClientRect();
    return {
      x: event.clientX - (rect?.left ?? 0),
      y: event.clientY - (rect?.top ?? 0),
    };
  };

  const handlePointerDown = (event: JSX.TargetedPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const point = pointFromEvent(event);
    setDragStart(point);
    setDragCurrent(point);
  };

  const handlePointerMove = (event: JSX.TargetedPointerEvent<HTMLDivElement>) => {
    if (!dragStart) return;
    setDragCurrent(pointFromEvent(event));
  };

  const handlePointerUp = (event: JSX.TargetedPointerEvent<HTMLDivElement>) => {
    if (!dragStart) return;
    const end = pointFromEvent(event);
    setDragStart(null);
    setDragCurrent(null);

    const box = boxFromPoints(dragStart, end);
    if (box.w < MIN_BOX_SIZE || box.h < MIN_BOX_SIZE) return;
    reportBoxes([...boxes, box]);
  };

  const handleClear = () => reportBoxes([]);

  const handleConfirm = async () => {
    setConfirming(true);
    setRedactError(false);
    try {
      const finalBlob = boxes.length > 0 ? await applyManualRedactions(blob, boxes) : blob;
      onConfirm(finalBlob);
    } catch {
      // Redaction failed. Do NOT fall back to the original blob — the user drew
      // those boxes to hide sensitive content, and sending the un-redacted image
      // would leak it. Keep them on the preview with an inline error to retry or
      // discard. Swallow so nothing propagates to the host page (never-throw).
      setRedactError(true);
    } finally {
      setConfirming(false);
    }
  };

  const draftBox = dragStart && dragCurrent ? boxFromPoints(dragStart, dragCurrent) : null;

  return (
    <div class="ft-screenshot-preview">
      <h3 class="ft-screenshot-preview-heading">{strings.screenshot.previewHeading}</h3>
      <p class="ft-screenshot-instructions">{strings.screenshot.instructions}</p>

      <div
        ref={canvasRef}
        class="ft-screenshot-canvas"
        role="group"
        aria-label={strings.screenshot.canvasLabel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {imageUrl ? <img src={imageUrl} alt={strings.screenshot.previewHeading} class="ft-screenshot-image" /> : null}

        {boxes.map((box, index) => (
          <div
            key={`${box.x}-${box.y}-${box.w}-${box.h}-${index}`}
            class="ft-screenshot-box"
            aria-label={strings.screenshot.boxLabel(index + 1)}
            style={{ left: `${box.x}px`, top: `${box.y}px`, width: `${box.w}px`, height: `${box.h}px` }}
          />
        ))}

        {draftBox ? (
          <div
            class="ft-screenshot-box"
            aria-hidden="true"
            style={{ left: `${draftBox.x}px`, top: `${draftBox.y}px`, width: `${draftBox.w}px`, height: `${draftBox.h}px` }}
          />
        ) : null}
      </div>

      {redactError ? (
        <p class="ft-screenshot-error" role="alert">
          {strings.screenshot.redactionErrorMessage}
        </p>
      ) : null}

      <div class="ft-screenshot-actions">
        <div class="ft-screenshot-actions-secondary">
          <button
            type="button"
            class="ft-screenshot-clear-button"
            onClick={handleClear}
            disabled={boxes.length === 0}
          >
            {strings.screenshot.clearBoxesLabel}
          </button>
          <button type="button" class="ft-screenshot-cancel-button" onClick={onCancel}>
            {strings.screenshot.cancelLabel}
          </button>
        </div>
        <button
          type="button"
          class="ft-screenshot-confirm-button"
          onClick={handleConfirm}
          disabled={confirming}
        >
          {strings.screenshot.confirmLabel}
        </button>
      </div>
    </div>
  );
}
